// Período compartilhado por Pedidos e Financeiro. As duas telas tinham listas
// de opções DIFERENTES (Pedidos não tinha mês, Financeiro não tinha ontem) e
// nenhuma das duas deixava escolher início e fim — só "uma data", o que impede
// exportar o relatório de um intervalo qualquer.
//
// Tudo em dia civil `YYYY-MM-DD`. Quem chama passa o `hoje` da sua própria
// fonte (as duas telas já tinham a sua), pra esta função não decidir fuso.

export type PeriodKey =
  | "hoje"
  | "ontem"
  | "7dias"
  | "30dias"
  | "90dias"
  | "mes"
  | "mes_anterior"
  | "mes_escolhido"
  | "personalizado"
  | "tudo";

export const PERIOD_OPTIONS: Array<{ value: PeriodKey; label: string }> = [
  { value: "hoje", label: "Hoje" },
  { value: "ontem", label: "Ontem" },
  { value: "7dias", label: "Últimos 7 dias" },
  { value: "30dias", label: "Últimos 30 dias" },
  { value: "90dias", label: "Últimos 90 dias" },
  { value: "mes", label: "Este mês" },
  { value: "mes_anterior", label: "Mês anterior" },
  { value: "mes_escolhido", label: "Escolher mês…" },
  { value: "personalizado", label: "Escolher período…" },
  { value: "tudo", label: "Tudo" },
];

export interface PeriodState {
  period: PeriodKey;
  /** YYYY-MM, para "Escolher mês…" */
  pickedMonth: string;
  /** YYYY-MM-DD, início do "Escolher período…" */
  customFrom: string;
  /** YYYY-MM-DD, fim do "Escolher período…" */
  customTo: string;
}

export const EMPTY_PERIOD_STATE: Omit<PeriodState, "period"> = {
  pickedMonth: "",
  customFrom: "",
  customTo: "",
};

export interface ResolvedPeriod {
  dateFrom: string;
  dateTo: string;
  label: string;
  /** "Tudo": quem consulta o servidor pode omitir o recorte. */
  isAll: boolean;
  /** Intervalo de mais de um dia — usado pra decidir texto no plural. */
  isRange: boolean;
}

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function lastDayOfMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  return `${ym}-${String(new Date(Date.UTC(y!, m!, 0)).getUTCDate()).padStart(2, "0")}`;
}

const br = (iso: string) => iso.split("-").reverse().join("/");

export function resolvePeriod(state: PeriodState, today: string): ResolvedPeriod {
  const ym = today.slice(0, 7);
  const one = (from: string, to: string, label: string): ResolvedPeriod => ({
    dateFrom: from,
    dateTo: to,
    label,
    isAll: false,
    isRange: from !== to,
  });

  switch (state.period) {
    case "hoje":
      return one(today, today, "Hoje");
    case "ontem": {
      const d = addDays(today, -1);
      return one(d, d, "Ontem");
    }
    case "7dias":
      return one(addDays(today, -6), today, "Últimos 7 dias");
    // 30 e 90 (10/09/2026): recortes das visitas ao cardápio no Resumo do
    // Excel de Pedidos; 90 é o teto da retenção das visitas.
    case "30dias":
      return one(addDays(today, -29), today, "Últimos 30 dias");
    case "90dias":
      return one(addDays(today, -89), today, "Últimos 90 dias");
    case "mes":
      // Mês CIVIL inteiro, não "até hoje": com contas a vencer no Histórico,
      // dia 1 recortava o mês a um dia só e a parcela do dia 10 "sumia"
      // (relato peu-burguer, 01/09). Pedidos não têm data futura — sem efeito lá.
      return one(`${ym}-01`, lastDayOfMonth(ym), "Este mês");
    case "mes_anterior": {
      const [y, m] = ym.split("-").map(Number);
      const prev = m === 1 ? `${y! - 1}-12` : `${y}-${String(m! - 1).padStart(2, "0")}`;
      return one(`${prev}-01`, lastDayOfMonth(prev), "Mês anterior");
    }
    case "mes_escolhido": {
      // Sem mês escolhido ainda, cai no mês atual em vez de devolver intervalo
      // vazio — a tela não pode zerar enquanto o lojista escolhe.
      if (!state.pickedMonth) return one(`${ym}-01`, today, "Este mês");
      const [y, m] = state.pickedMonth.split("-").map(Number);
      return one(
        `${state.pickedMonth}-01`,
        lastDayOfMonth(state.pickedMonth),
        `${String(m).padStart(2, "0")}/${y}`,
      );
    }
    case "personalizado": {
      const from = state.customFrom || today;
      const to = state.customTo || from;
      // Datas invertidas viram intervalo válido em vez de resultado vazio: o
      // lojista costuma preencher o fim antes do início.
      const [ini, fim] = from <= to ? [from, to] : [to, from];
      return one(ini, fim, ini === fim ? br(ini) : `${br(ini)} a ${br(fim)}`);
    }
    case "tudo":
    default:
      // Teto no futuro distante, não em "hoje": com contas a VENCER nas telas
      // (parcelas, pendências), "Tudo" com teto = hoje escondia tudo que vence
      // amanhã em diante (medido 01/09/2026: loja só com parcelas futuras
      // mostrava "Tudo" vazio).
      return { dateFrom: "2000-01-01", dateTo: "9999-12-31", label: "Tudo", isAll: true, isRange: true };
  }
}

/** Sufixo para nome de arquivo exportado: `2026-08-01_2026-08-12` ou o dia. */
export function periodFileTag(r: ResolvedPeriod): string {
  if (r.isAll) return "tudo";
  return r.dateFrom === r.dateTo ? r.dateFrom : `${r.dateFrom}_${r.dateTo}`;
}
